import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { Asset } from '@core/generated/gql-types';
import { getAssetDateDisplay } from '@core/util/assetMetadata.util';

interface OrphanDocumentRowProps {
  asset: Asset;
  uploadedBy: string;
  uploadedDate: string | null;
  fileName: string;
  onPreview: (asset: Asset, title: string) => void;
  onDownload: (asset: Asset) => void;
  onReject: (asset: Asset) => void;
}

const OrphanDocumentRow: React.FC<OrphanDocumentRowProps> = ({
  asset,
  uploadedBy,
  uploadedDate,
  fileName,
  onPreview,
  onDownload,
  onReject,
}) => {
  const title = asset.tag?.replace(/([A-Z])/g, ' $1').trim() || 'Document';
  const { issueDateLabel, expiryDateLabel, isExpired } =
    getAssetDateDisplay(asset);

  return (
    <div className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-b-0">
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">{title}</div>
        {uploadedDate && (
          <div className="mt-1 text-xs text-gray-500">
            {fileName} uploaded by {uploadedBy} on {uploadedDate}
          </div>
        )}
        {(issueDateLabel || expiryDateLabel) && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {issueDateLabel && (
              <span className="text-gray-500">{issueDateLabel}</span>
            )}
            {expiryDateLabel && (
              <span className={isExpired ? 'text-red-600' : 'text-gray-500'}>
                {expiryDateLabel}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPreview(asset, title)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
          Preview
        </button>
        <button
          type="button"
          onClick={() => void onDownload(asset)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
          Download
        </button>
        <button
          type="button"
          onClick={() => onReject(asset)}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
          Reject
        </button>
      </div>
    </div>
  );
};

export default OrphanDocumentRow;
